import { defineCustomElement, ref, computed, watch } from 'vue/dist/vue.esm-bundler.js'
import { html } from './shared/functions'

// 본문에서 첫 링크를 찾는다. 문장 끝에 붙는 마침표·괄호는 주소가 아니므로 뗀다.
const URL_PATTERN = /https?:\/\/[^\s<>"']+/

const findUrl = (text) => {
  const found = String(text || '').match(URL_PATTERN)
  return found ? found[0].replace(/[.,)\]]+$/, '') : ''
}

const ImComponent = defineCustomElement({
  props: {
    content: {
      type: String,
      default: ""
    },
    to_address: {
      type: String,
      default: ""
    },
    // 비워두면 본문에서 첫 링크를 찾아 쓴다
    preview_url: {
      type: String,
      default: ""
    },
  },
  setup(props) {
    const preview = ref(null)
    let debounce = null
    let latest = 0

    const targetUrl = computed(() => props.preview_url || findUrl(props.content))

    const loadPreview = async (url) => {
      const mine = ++latest

      // 같은 오리진만 읽는다. 남의 도메인은 CORS 로 막히고, 우리 페이지는
      // 로그인 게이트에 걸려도 fallback 이 같은 카드를 돌려준다.
      if (!url || !url.startsWith(location.origin)) {
        preview.value = null
        return
      }

      try {
        const response = await fetch(url, { credentials: 'same-origin', redirect: 'follow' })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const document_ = new DOMParser().parseFromString(await response.text(), 'text/html')
        const meta = (property) =>
          document_.querySelector(`meta[property="${property}"]`)?.getAttribute('content') || ''

        const found = {
          url,
          host: new URL(url).host,
          title: meta('og:title'),
          description: meta('og:description'),
          image: meta('og:image'),
        }
        // 늦게 도착한 응답이 최신 링크의 카드를 덮지 않게 한다
        if (mine === latest) preview.value = found.title ? found : null
      } catch (error) {
        console.warn('[im-notify-sms-bubble] 링크 미리보기 실패:', error)
        if (mine === latest) preview.value = null
      }
    }

    // 본문을 고치는 중에는 매 글자마다 부르지 않는다
    watch(targetUrl, (url) => {
      clearTimeout(debounce)
      debounce = setTimeout(() => loadPreview(url), 500)
    }, { immediate: true })

    return { preview }
  },
  template: html`
    <div role="chat-bubble">
      <div class="flex items-start gap-2.5">
        <slot name="to_image">
          <svg class="w-8 h-8" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z"></path>
          </svg>
        </slot>
        <div class="flex flex-col gap-1 w-full max-w-[320px]">
            <div class="flex items-center space-x-2 rtl:space-x-reverse">
              <span class="text-sm font-semibold text-gray-900 dark:text-white">동행돌봄인</span>
              <span class="text-sm font-normal text-gray-500 dark:text-gray-400">지금</span>
            </div>
            <div class="flex flex-col leading-1.5 p-4 border-gray-200 bg-gray-100 rounded-e-xl rounded-es-xl dark:bg-gray-700">
              <p class="text-sm font-normal text-gray-900 dark:text-white whitespace-pre-line">{{ content }}</p>
            </div>
            <!--
              카드는 말풍선 밖의 형제다 — 실제 카카오톡이 본문 말풍선과 링크 카드를
              별개 박스로 보여주기 때문이다. 바깥 컬럼의 gap 이 둘 사이 간격을 만들고,
              카드 폭은 flex stretch 로 말풍선과 같아진다.
            -->
            <a
              v-if="preview"
              :href="preview.url"
              target="_blank"
              rel="noopener"
              class="block overflow-hidden rounded-lg border border-gray-300 bg-white no-underline dark:bg-gray-800 dark:border-gray-600"
            >
              <img v-if="preview.image" :src="preview.image" alt="" class="block w-full aspect-[40/21] object-cover">
              <span class="block px-3 py-2">
                <span class="block text-xs text-gray-500 dark:text-gray-400">{{ preview.host }}</span>
                <span class="block text-sm font-semibold text-gray-900 dark:text-white">{{ preview.title }}</span>
                <span class="block text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{{ preview.description }}</span>
              </span>
            </a>
            <span class="text-sm font-normal text-gray-500 dark:text-gray-400">{{ to_address }}에 발송</span>
        </div>
      </div>
    </div>
  `,
},  { shadowRoot: false })

customElements.define('im-notify-sms-bubble', ImComponent)
